import React, { useEffect } from "react";
import { Modal, Table, Tag } from "antd";
import { useDispatch, useSelector } from "react-redux";

import { fetchBaselineDetail } from "../../../../core/data/redux/projects/thunks";
import {
  selectActiveBaseline,
  selectBaselinesLoading,
  selectIssuesByProject,
} from "../../../../core/data/redux/projects/selectors";
import type { AppDispatch } from "../../../../core/data/redux/store";
import dayjs from "dayjs";

interface Props {
  baselineId: number | null;
  projectId: number;
  open: boolean;
  onClose: () => void;
}

const BaselineCompareModal: React.FC<Props> = ({ baselineId, projectId, open, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const activeBaseline = useSelector(selectActiveBaseline);
  const loading = useSelector(selectBaselinesLoading);
  const currentIssues = useSelector(selectIssuesByProject(projectId));

  useEffect(() => {
    if (baselineId && open) {
      dispatch(fetchBaselineDetail(baselineId));
    }
  }, [dispatch, baselineId, open]);

  const columns = [
    {
      title: "Issue",
      key: "issue",
      render: (_: any, record: any) => (
        <div>
          <div className="fw-semibold" style={{ fontSize: 13 }}>{record.issue_key}</div>
          <div className="text-muted" style={{ fontSize: 12 }}>{record.title}</div>
        </div>
      ),
    },
    {
      title: "Planned Start (Baseline)",
      dataIndex: "planned_start",
      key: "planned_start",
      render: (val: string) => val ? dayjs(val).format("MMM D, YYYY") : "—",
    },
    {
      title: "Current Start",
      key: "current_start",
      render: (_: any, record: any) => {
        const val = record.current_start;
        if (!val) return "—";
        const planned = record.planned_start ? dayjs(record.planned_start) : null;
        const current = dayjs(val);
        const diff = planned ? current.diff(planned, "day") : 0;

        return (
          <div>
            <span>{current.format("MMM D, YYYY")}</span>
            {diff > 0 && <Tag color="error" className="ms-2">+{diff}d late</Tag>}
            {diff < 0 && <Tag color="success" className="ms-2">{diff}d early</Tag>}
          </div>
        );
      },
    },
    {
      title: "Planned End (Baseline)",
      dataIndex: "planned_end",
      key: "planned_end",
      render: (val: string) => val ? dayjs(val).format("MMM D, YYYY") : "—",
    },
    {
      title: "Current End",
      key: "current_end",
      render: (_: any, record: any) => {
        const val = record.current_end;
        if (!val) return "—";
        const planned = record.planned_end ? dayjs(record.planned_end) : null;
        const current = dayjs(val);
        const diff = planned ? current.diff(planned, "day") : 0;

        return (
          <div>
            <span>{current.format("MMM D, YYYY")}</span>
            {diff > 0 && <Tag color="error" className="ms-2">+{diff}d late</Tag>}
            {diff < 0 && <Tag color="success" className="ms-2">{diff}d early</Tag>}
          </div>
        );
      },
    },
  ];

  const tableData = (activeBaseline?.snapshots || []).map((snap: any) => {
    const current = currentIssues.find((i) => i.id === snap.issue_id);
    return {
      key: snap.id,
      issue_id: snap.issue_id,
      issue_key: snap.issue_key,
      title: snap.title,
      planned_start: snap.planned_start,
      planned_end: snap.planned_end,
      current_start: current?.start_date,
      current_end: current?.due_date,
    };
  });

  return (
    <Modal
      title={
        <div>
          <h5 className="mb-1">Baseline Comparison</h5>
          <div className="text-muted fw-normal" style={{ fontSize: 13 }}>
            {activeBaseline?.name} (Snapshotted: {activeBaseline ? dayjs(activeBaseline.snapshot_date).format("MMM D, YYYY") : ""})
          </div>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      maskClosable={false}
      keyboard={false}
      centered
      width="640px"
      zIndex={1001}
      styles={{
        content: { padding: 0, borderRadius: 12, overflow: "hidden" },
        header: { padding: "14px 20px 12px", borderBottom: "1px solid #f0f0f0", marginBottom: 0 },
        body: { padding: "16px 24px 20px" },
        footer: { padding: "10px 20px 14px", borderTop: "1px solid #f0f0f0", marginTop: 0 },
        mask: {
          position: "fixed",
          inset: 0,
          width: "100vw",
          height: "100vh",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          background: "rgba(0,0,0,0.45)",
        },
      }}
    >
      <Table
        columns={columns}
        dataSource={tableData}
        loading={loading}
        pagination={false}
        scroll={{ y: 400 }}
        size="small"
      />
    </Modal>
  );
};

export default BaselineCompareModal;
